import { useMemo, useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { Search, Download, ChevronDown } from "lucide-react";
import { cx } from "../../utils";
import { GetVisitorTraffic } from "../../services/malltrafic";
import { fetchdropdownMall } from "../../services/dropdown";

export interface MallTrafficData {
  VisitorTrafficId: number;
  Month: number;
  Year: number;
  FileAttach: string;
  DataAttach: string | null;
  Status: number;
  ModifiedOn: string;
}

interface MallOption {
  value: string;
  label: string;
}

type SortKey = "Month" | "Year";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2021;
  const years = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year.toString());
  }
  return years;
};

export default function MallTraffic() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>("Month");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [mallTrafficData, setMallTrafficData] = useState<MallTrafficData[]>([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState<string>("");
  const [availableYears] = useState<string[]>(generateYearOptions());

  const [mallOptions, setMallOptions] = useState<MallOption[]>([]);
  const [selectedMall, setSelectedMall] = useState<string>("");
  const [mallSearchQuery, setMallSearchQuery] = useState("");
  const [isMallDropdownOpen, setIsMallDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchMallOptions = async () => {
      try {
        const response = await fetchdropdownMall(0);

        if (response && response.dropdownClassDatas) {
          const options = response.dropdownClassDatas.map(
            (mall: { DataId: number; DataLabel: string }) => ({
              value: mall.DataId.toString(),
              label: mall.DataLabel,
            })
          );
          setMallOptions(options);

          const storedMallId =
            localStorage.getItem("mall_id") ||
            sessionStorage.getItem("mall_id");
          if (
            storedMallId &&
            options.find((opt: MallOption) => opt.value === storedMallId)
          ) {
            setSelectedMall(storedMallId);
          } else {
            setSelectedMall("");
            localStorage.removeItem("mall_id");
          }
        }
      } catch (error) {
        console.error("Error fetching mall options:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch mall options",
        });
      }
    };

    fetchMallOptions();
  }, []);

  const fetchVisitorTraffic = useCallback(async () => {
    if (!selectedMall || !yearFilter) return;

    setLoading(true);
    try {
      const year = parseInt(yearFilter);
      const response = await GetVisitorTraffic(
        0,
        parseInt(selectedMall),
        year,
        0,
        0
      );

      if (response && response.visitorTrafficDatas) {
        setMallTrafficData(response.visitorTrafficDatas);
      } else {
        setMallTrafficData([]);
      }
    } catch (error) {
      console.error("Error fetching visitor traffic:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch visitor traffic data",
      });
      setMallTrafficData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMall, yearFilter]);

  useEffect(() => {
    if (selectedMall && yearFilter) {
      fetchVisitorTraffic();
    } else if (selectedMall && !yearFilter) {
      setMallTrafficData([]);
    }
  }, [selectedMall, yearFilter, fetchVisitorTraffic]);

  const filteredMallOptions = useMemo(() => {
    if (!mallSearchQuery) return mallOptions;
    return mallOptions.filter((option) =>
      option.label.toLowerCase().includes(mallSearchQuery.toLowerCase())
    );
  }, [mallOptions, mallSearchQuery]);

  const filtered = useMemo(() => {
    const qq = q.toLowerCase();
    return mallTrafficData.filter((item) => {
      const monthName = monthNames[item.Month - 1];
      return (
        monthName.toLowerCase().includes(qq) ||
        item.Year.toString().includes(qq)
      );
    });
  }, [mallTrafficData, q]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      let aVal: number, bVal: number;
      if (sortKey === "Month") {
        aVal = a.Month;
        bVal = b.Month;
      } else {
        aVal = a.Year;
        bVal = b.Year;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * perPage;
  const end = Math.min(start + perPage, total);
  const pageRows = sorted.slice(start, end);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleDownload = (item: MallTrafficData) => {
    if (item.FileAttach) {
      try {
        const byteCharacters = atob(item.FileAttach);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `visitor_traffic_${
          monthNames[item.Month - 1]
        }_${item.Year}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        Swal.fire({
          icon: "success",
          title: "Download Started",
          text: `Downloading traffic report for ${
            monthNames[item.Month - 1]
          } ${item.Year}`,
          showConfirmButton: false,
          timer: 1500,
        });
      } catch {
        window.open(item.FileAttach, "_blank");
        Swal.fire({
          icon: "success",
          title: "Download Started",
          text: `Downloading traffic report for ${
            monthNames[item.Month - 1]
          } ${item.Year}`,
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "No File",
        text: "No file attached for this traffic report",
      });
    }
  };

  const handleMallSelect = (mallValue: string) => {
    setSelectedMall(mallValue);
    localStorage.setItem("mall_id", mallValue);
    setYearFilter("");
    setPage(1);
    setIsMallDropdownOpen(false);
    setMallSearchQuery("");
  };

  const selectedMallLabel =
    mallOptions.find((opt) => opt.value === selectedMall)?.label ||
    "Select Mall";
  const isMallDisabled = mallOptions.length === 1;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".mall-dropdown-container")) {
        setIsMallDropdownOpen(false);
        setMallSearchQuery("");
      }
    };
    if (isMallDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMallDropdownOpen]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
            <div className="lg:col-span-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Mall
              </label>
              <div className="relative mall-dropdown-container">
                <button
                  type="button"
                  onClick={() =>
                    !isMallDisabled &&
                    setIsMallDropdownOpen(!isMallDropdownOpen)
                  }
                  disabled={isMallDisabled}
                  className={cx(
                    "w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100 flex items-center justify-between",
                    isMallDisabled
                      ? "cursor-not-allowed opacity-60"
                      : "cursor-pointer"
                  )}
                >
                  <span className={!selectedMall ? "text-gray-400" : ""}>
                    {selectedMallLabel}
                  </span>
                  <ChevronDown
                    className={cx(
                      "w-4 h-4 text-gray-400 transition-transform",
                      isMallDropdownOpen && "rotate-180"
                    )}
                  />
                </button>

                {isMallDropdownOpen && !isMallDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={mallSearchQuery}
                          onChange={(e) => setMallSearchQuery(e.target.value)}
                          placeholder="Search mall..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredMallOptions.length > 0 ? (
                        filteredMallOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleMallSelect(option.value)}
                            className={cx(
                              "w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                              selectedMall === option.value
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                : "text-gray-700 dark:text-gray-300"
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
                          No mall found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Year
              </label>
              <select
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-gray-100"
              >
                <option value="">select year</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-3 lg:ml-auto">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hidden sm:block" />
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search"
                  className="w-full sm:pl-10 pl-3 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-gray-100"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          {loading ? (
            <div className="py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </p>
            </div>
          ) : (
            <>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="py-3 px-4 text-left">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          No
                        </span>
                      </th>
                      <Th
                        sortable
                        activeKey={sortKey}
                        dir={sortDir}
                        k="Month"
                        onClick={toggleSort}
                      >
                        Month
                      </Th>
                      <Th
                        sortable
                        activeKey={sortKey}
                        dir={sortDir}
                        k="Year"
                        onClick={toggleSort}
                      >
                        Year
                      </Th>
                      <th className="py-3 px-4 text-left">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Status
                        </span>
                      </th>
                      <th className="py-3 px-4 text-center">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Action
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {pageRows.map((item, idx) => (
                      <tr
                        key={item.VisitorTrafficId}
                        className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                      >
                        <td className="py-4 px-4 text-sm text-gray-900 dark:text-gray-100">
                          {start + idx + 1}
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {monthNames[item.Month - 1]}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                          {item.Year}
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={cx(
                              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                              item.Status === 1
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                            )}
                          >
                            {item.Status === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDownload(item)}
                              disabled={!item.FileAttach}
                              className={cx(
                                "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors",
                                item.FileAttach
                                  ? "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                                  : "text-gray-400 cursor-not-allowed"
                              )}
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pageRows.length === 0 && (
                      <tr>
                        <td
                          className="py-12 px-4 text-center text-sm text-gray-500 dark:text-gray-400"
                          colSpan={5}
                        >
                          {!yearFilter
                            ? "Please select a year"
                            : "No data available"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="block sm:hidden p-4">
                {pageRows.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                    {!yearFilter ? "Please select a year" : "No data available"}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {pageRows.map((item, idx) => (
                      <div
                        key={item.VisitorTrafficId}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 flex flex-col gap-2 shadow-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                            #{start + idx + 1}
                          </span>
                          <span
                            className={cx(
                              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                              item.Status === 1
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                            )}
                          >
                            {item.Status === 1 ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">
                              Month
                            </span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium">
                              {monthNames[item.Month - 1]}
                            </span>
                          </div>
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">
                              Year
                            </span>
                            <span className="text-gray-800 dark:text-gray-100 font-medium">
                              {item.Year}
                            </span>
                          </div>
                        </div>

                        <div className="pt-1 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => handleDownload(item)}
                            disabled={!item.FileAttach}
                            className={cx(
                              "w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors",
                              item.FileAttach
                                ? "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                                : "bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed"
                            )}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {total === 0 ? "0" : `${start + 1}–${end}`} of {total}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={clampedPage <= 1}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Prev
                    </button>
                    <span className="text-xs text-gray-700 dark:text-gray-200 font-medium">
                      {clampedPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={clampedPage >= totalPages}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="hidden sm:flex p-6 flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Show result:
              </span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={clampedPage === 1}
                className={cx(
                  "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                  clampedPage === 1
                    ? "text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed dark:text-gray-600 dark:bg-gray-800 dark:border-gray-700"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                )}
              >
                &lt;
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (clampedPage <= 3) {
                  pageNum = i + 1;
                } else if (clampedPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = clampedPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cx(
                      "w-9 h-9 text-sm rounded-lg transition-colors",
                      pageNum === clampedPage
                        ? "bg-blue-600 text-white"
                        : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && clampedPage < totalPages - 2 && (
                <>
                  <span className="text-gray-400">...</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-9 h-9 text-sm rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={clampedPage === totalPages}
                className={cx(
                  "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                  clampedPage === totalPages
                    ? "text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed dark:text-gray-600 dark:bg-gray-800 dark:border-gray-700"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                )}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>

        <div className="border border-dashed border-green-700 dark:border-green-500 rounded-xl p-4 sm:py-5 sm:px-6 bg-green-50/10 dark:bg-green-950/10">
          <p className="text-xs sm:text-sm text-green-700 dark:text-green-400 font-medium leading-relaxed">
            Akses data Mall Traffic hanya dapat dilakukan setelah Anda mengunggah data Sales bulan terkait. Silakan lengkapi Sales Upload terlebih dahulu untuk melanjutkan.
          </p>
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  sortable,
  k,
  activeKey,
  dir,
  onClick,
}: {
  children: React.ReactNode;
  sortable?: boolean;
  k?: SortKey;
  activeKey?: SortKey;
  dir?: "asc" | "desc";
  onClick?: (k: SortKey) => void;
}) {
  const isActive = sortable && k === activeKey;
  return (
    <th
      className={cx(
        "py-3 px-4 text-left select-none",
        sortable && "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
      )}
      onClick={
        sortable && k && onClick ? () => onClick(k as SortKey) : undefined
      }
    >
      <div className="inline-flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {children}
        </span>
        {sortable && (
          <svg
            className={cx(
              "w-3 h-3",
              isActive ? "text-gray-600 dark:text-gray-300" : "text-gray-400"
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            {isActive ? (
              dir === "asc" ? (
                <path d="M5 15l7-7 7 7" />
              ) : (
                <path d="M19 9l-7 7-7-7" />
              )
            ) : (
              <>
                <path d="M5 15l7-7 7 7" />
                <path d="M5 9l7 7 7-7" opacity="0.4" />
              </>
            )}
          </svg>
        )}
      </div>
    </th>
  );
}